import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function ProfileAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Snapshot - Corp. intelligence API" description="Query snapshot data via the Corp. intelligence API; request parameters, code examples and the response schema for the snapshot endpoint."  canonicalUrl="https://axionquant.com/docs/03-fundamentals/corp.-intelligence/01-snapshot" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Snapshot - Corp. intelligence API</DocHeading>
      <div className="space-y-12" ref={sectionRef}>


        {/* ========== Get Stock Summary ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /profiles/:ticker/summary
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves current trading statistics, valuation multiples, dividend information,
                and 52-week price range. Ideal for a quick snapshot of a stock's market performance.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm"><span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">Required</span></td>
                      <td className="px-4 py-3 text-sm text-gray-700">Stock ticker symbol</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/profiles/AAPL/summary', '/profiles/MSFT/summary']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Key Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">previousClose, open, dayLow, dayHigh</p>
                  <p className="text-sm text-gray-600">Daily trading prices</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">volume, averageVolume</p>
                  <p className="text-sm text-gray-600">Current and average trading volume</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">marketCap</p>
                  <p className="text-sm text-gray-600">Market capitalization</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">trailingPE, forwardPE</p>
                  <p className="text-sm text-gray-600">Price-to-earnings ratios (past & forward)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">dividendRate, dividendYield, exDividendDate</p>
                  <p className="text-sm text-gray-600">Dividend information</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">fiftyTwoWeekLow, fiftyTwoWeekHigh</p>
                  <p className="text-sm text-gray-600">52-week price range</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">beta</p>
                  <p className="text-sm text-gray-600">Volatility measure relative to market</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Stock Summary (Apple)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Stock Summary (Apple)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="profile/ticker/summary"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="03-fundamentals/corp.-intelligence/01-snapshot"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
